import { zodResolver } from "@hookform/resolvers/zod"
import {
  Analytics,
  ShowChart,
  Lock,
  PriceChange,
  History,
  Group,
  BarChartSharp,
} from "@mui/icons-material"
import {
  Avatar,
  Box,
  Button,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
  useTheme,
} from "@mui/material"
import { createDataItemSigner, message, result } from "@permaweb/aoconnect"
import { useMutation } from "@tanstack/react-query"
import { ConnectButton, useActiveAddress } from "arweave-wallet-kit"
import React from "react"
import { useForm } from "react-hook-form"
import { useParams } from "react-router-dom"
import { useNavigate } from "react-router-dom"
import { useQuery } from "urql"
import z from "zod"

import { goldsky } from "@/api/graphql-client"
import BridgeTokensCTA from "@/components/bridge-tokens-cta"
import ENV from "@/env"
import useHopperPrice from "@/hooks/useHopper"
import useTokenBalance from "@/hooks/useTokenBalance"
import { DEXI_SUBSCRIBE_FULL_PRICE, PAYMENT_TOKENS } from "@/settings"
import { formatUnits, parseUnits } from "@/utils/math"

const formSchema = z.object({
  PaymentToken: z.string(),
})

const USD_PRICE = DEXI_SUBSCRIBE_FULL_PRICE

const graphqlQuery = `
query transactions($ammProcess:String!){
  transactions(tags: [{
    name: "Action",
    values: ["Activate-AMM-Result"]
  },{
    name:"AMM-Process",
    values:[$ammProcess]
  },{
    name:"Success",
    values:["true"]
  }]) 
  {
    edges {
      node {
        id
        tags {
          name
          value
        }
      }
    }
  }
}`

async function checkForActivateAMMResult(poolId: string) {
  const result = await goldsky
    .query<GraphqlResult>(graphqlQuery, {
      ammProcess: poolId,
    })
    .toPromise()

  if (result.data?.transactions.edges && result.data.transactions.edges.length > 0) {
    return true
  }

  setTimeout(async () => {
    await checkForActivateAMMResult(poolId)
  }, 3000)
}

interface GraphqlResult {
  transactions: {
    edges: {
      node: {
        id: string
        tags: {
          name: string
          value: string
        }[]
      }
    }[]
  }
}

const ACTIVE_PAYMENT_TOKENS = PAYMENT_TOKENS.filter((token) => !token.disabled)

export default function OnboardingPoolPage() {
  const { poolId } = useParams()
  const theme = useTheme()
  const navigate = useNavigate()
  const userAddress = useActiveAddress()
  const [results, executeQuery] = useQuery<GraphqlResult>({
    query: graphqlQuery,
    variables: {
      ammProcess: poolId,
    },
  })

  const { register, handleSubmit, watch } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      PaymentToken: ACTIVE_PAYMENT_TOKENS[0].value,
    },
  })

  const paymentToken = watch("PaymentToken")

  const selectedToken =
    PAYMENT_TOKENS.find((token) => token.value === paymentToken) || ACTIVE_PAYMENT_TOKENS[0]

  const activateAMM = useMutation({
    mutationKey: ["activate-amm", poolId],
    mutationFn: async ({
      paymentToken,
      paymentValue,
    }: {
      paymentToken: string
      paymentValue: string
    }) => {
      const dexiRegister = await message({
        process: paymentToken,
        tags: [
          { name: "Action", value: "Transfer" },
          {
            name: "Quantity",
            value: paymentValue.toString(),
          },
          {
            name: "Recipient",
            value: ENV.VITE_DEXI_PROCESS,
          },
          {
            name: "X-Action",
            value: "Activate-AMM",
          },
          {
            name: "X-AMM-Process",
            value: poolId as string,
          },
        ],
        data: "",
        signer: createDataItemSigner(window.arweaveWallet),
      })

      const dexiRegisterResult = await result({
        process: paymentToken,
        message: dexiRegister,
      })

      return dexiRegisterResult
    },
  })

  const {
    data: paymentTokenPrice,
    isLoading: isPaymentTokenPriceLoading,
    isRefetching: isPaymentTokenRefeching,
  } = useHopperPrice(selectedToken.value, "token")

  const { data: paymentTokenBalance } = useTokenBalance(selectedToken.value)

  const totalPriceReal = paymentTokenPrice ? USD_PRICE * paymentTokenPrice : 0
  const totalPrice = totalPriceReal + totalPriceReal * 0.05

  const hasEnoughPaymentTokenBalance =
    Number(formatUnits(BigInt(paymentTokenBalance || "0"), selectedToken.denomination)) >=
    totalPrice

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    console.log("onSubmit", values)

    const response = await activateAMM.mutateAsync({
      paymentToken: values.PaymentToken,
      paymentValue: parseUnits(totalPrice.toString(), selectedToken.denomination).toString(),
    })

    if (response) {
      await checkForActivateAMMResult(poolId as string)
      navigate(`/pool/${poolId}`)
    }
  }

  if (!poolId) {
    return <div>Invalid pool</div>
  }

  return (
    <Box sx={{ maxWidth: 1200, margin: "auto", padding: 4 }}>
      <Typography variant="h4" textAlign="center" gutterBottom>
        Unlock the Power of DEXI
      </Typography>
      <Typography variant="subtitle1" textAlign="center" color="text.secondary" gutterBottom mb={4}>
        Subscribe now and gain valuable insights!
      </Typography>

      <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 4 }}>
        <Box
          sx={{
            flex: { xs: "1 1 auto", md: "0 0 35%" },
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Paper
            sx={{
              padding: 3,
              backgroundColor: theme.palette.background.default,
              display: "flex",
              flexDirection: "column",
              height: "100%",
            }}
          >
            <Typography variant="h6" gutterBottom marginBottom={3}>
              Subscribe to DEXI
            </Typography>
            <form
              onSubmit={handleSubmit(onSubmit)}
              style={{ display: "flex", flexDirection: "column", flexGrow: 1 }}
            >
              <Stack spacing={3} sx={{ flexGrow: 1 }}>
                <FormControl fullWidth>
                  <InputLabel id="payment-token-label">Payment Token</InputLabel>
                  <Select
                    labelId="payment-token-label"
                    label="Payment Token"
                    {...register("PaymentToken")}
                    defaultValue={ACTIVE_PAYMENT_TOKENS[0].value}
                  >
                    {PAYMENT_TOKENS.map((token) => (
                      <MenuItem key={token.value} value={token.value} disabled={token.disabled}>
                        <Stack direction="row" gap={1} alignItems="center">
                          <Avatar
                            src={`https://arweave.net/${token.logo}`}
                            alt={token.label}
                            sx={{
                              width: 18,
                              height: 18,
                              fontSize: 18 * 0.75,
                            }}
                          />
                          {token.label}
                        </Stack>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <BridgeTokensCTA token={selectedToken.value} />
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Subscription Price:
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="h5" color="primary">
                      ${USD_PRICE}
                    </Typography>
                    <Chip label="One-time fee" color="secondary" size="small" />
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ marginTop: 2 }}>
                    Price will be converted to the selected payment token.
                  </Typography>
                </Box>
                {isPaymentTokenPriceLoading || isPaymentTokenRefeching ? (
                  <Typography variant="body2" color="text.secondary" sx={{ marginTop: 2 }}>
                    Loading price...
                  </Typography>
                ) : (
                  <Stack direction="column" spacing={0.5}>
                    <Typography variant="body1">Price in {selectedToken?.label}:</Typography>
                    <Typography variant="h6" color="text.primary">
                      {`${totalPrice.toFixed(4)} ${selectedToken?.label}`}
                    </Typography>
                  </Stack>
                )}
                <Box sx={{ flexGrow: 1 }} /> {/* Spacer */}
                {results.data?.transactions.edges && results.data.transactions.edges.length > 0 ? (
                  <div>This Pool is already subscribed.</div>
                ) : (
                  <>
                    {!userAddress ? (
                      <ConnectButton
                        id="connect-wallet-button"
                        showBalance={false}
                        showProfilePicture={false}
                      />
                    ) : (
                      <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        disabled={!hasEnoughPaymentTokenBalance || activateAMM.isPending}
                      >
                        {activateAMM.isPending
                          ? "Subscribing..."
                          : !hasEnoughPaymentTokenBalance
                            ? "Insufficient balance"
                            : "Subscribe"}
                      </Button>
                    )}
                    {activateAMM.isError && (
                      <Typography color="error" variant="body2">
                        {activateAMM.error.message}
                      </Typography>
                    )}
                    {activateAMM.isSuccess && (
                      <Typography color="success" variant="body2">
                        Payment successfull! Please wait for confirmation...
                      </Typography>
                    )}
                  </>
                )}
              </Stack>
            </form>
          </Paper>
        </Box>

        <Box
          sx={{
            flex: { xs: "1 1 auto", md: "0 0 65%" },
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Paper
            sx={{
              padding: 3,
              display: "flex",
              flexDirection: "column",
              height: "100%",
              position: "relative",
            }}
          >
            <Typography variant="h6" gutterBottom marginBottom={3}>
              By subscribing, you'll unlock:
            </Typography>
            <Stack spacing={2}>
              {[
                { icon: <Analytics />, text: "Detailed analytics and performance tracking" },
                {
                  icon: <ShowChart />,
                  text: "Advanced candlestick charts with custom timeframes",
                },
                { icon: <BarChartSharp />, text: "In-depth liquidity information" },
                { icon: <Lock />, text: "Liquidity lock details" },
                { icon: <PriceChange />, text: "Real-time token prices" },
                { icon: <History />, text: "Historical transaction data" },
                { icon: <Group />, text: "Token holder insights" },
              ].map((item, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    {item.icon}
                    <Typography variant="body1">{item.text}</Typography>
                  </Stack>
                </Grid>
              ))}
            </Stack>
            {/*  <img
              src={subscribeSVG}
              alt="Subscribe to DEXI"
              style={{
                marginTop: "auto",
                position: "absolute",
                bottom: 20,
                right: 20,
                height: "60%",
              }}
            /> */}
          </Paper>
        </Box>
      </Box>
    </Box>
  )
}
